import { NodeType } from '@common-models/bot-node';
import { duplicateNodeThunk, handleNodeDeletionThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { ContentCopy as CopyIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import { Menu, MenuItem, Modal } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactFlow } from 'reactflow';
import { nodeTypes } from '../bt-nodes/bt-nodes-def';
import BTChatbotModal from './bt-chatbot-modal';
import { namespace } from '@common/config/constants';

interface Props {
    type: NodeType;
    open: boolean;
    onClose: () => void;
    anchorEl: null | HTMLElement;
    id: string;
}

export default function NodeMenu({ type, open, onClose, anchorEl, id }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const { setNodes, setEdges } = useReactFlow();

    const [modalOpen, setModalOpen] = useState(false);

    const modalType = () => {
        switch (type) {
            case 'condition':
                return 'setCondition';
            case 'question':
                return 'setQuestion';
            case 'webhook':
                return 'setWebhook';
            case 'googleSheets':
                return 'setGoogleSheet';
            case 'trigger':
                return 'setTrigger';
            case 'agent':
                return 'setAgent';
            case 'LLM':
                return 'setLLM';
            default:
                return 'setSettings';
        }
    };

    const duplicateNode = async () => {
        const resultAction = await dispatch(duplicateNodeThunk({ id, type }));
        if (duplicateNodeThunk.fulfilled.match(resultAction)) {
            setNodes((nodes) => [...nodes, resultAction.payload]);
        }
        onClose();
    };

    const deleteNode = () => {
        dispatch(handleNodeDeletionThunk({ deletedNodeId: id, setNodes, setEdges }));
        onClose();
    };

    const handleOpenModal = () => {
        onClose();
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: -1,
                            width: 180,
                            borderRadius: '10px',
                            border: '1px solid var(--color-border, #e2e8f0)',
                            boxShadow: '0 12px 32px -8px rgba(15, 23, 42, 0.16), 0 4px 12px -4px rgba(15, 23, 42, 0.08)',
                            backgroundColor: 'var(--color-surface, #ffffff)',
                            color: 'var(--color-text, #111827)',
                            overflow: 'hidden',
                        },
                    },
                }}
                sx={{
                    '& .MuiList-root': {
                        padding: '4px',
                    },
                    '& .MuiMenuItem-root': {
                        padding: '8px 10px',
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: 'var(--color-text, #1f2937)',
                        gap: '10px',
                        transition: 'background-color 0.12s ease, color 0.12s ease',
                        '& svg': {
                            fontSize: 18,
                            color: 'var(--color-text-muted, #64748b)',
                            transition: 'color 0.12s ease',
                        },
                        '&:hover': {
                            backgroundColor: 'var(--color-surface-muted, #f1f5f9)',
                            '& svg': {
                                color: 'var(--color-text, #1f2937)',
                            },
                        },
                    },
                }}
            >
                {nodeTypes?.[type]?.isEditable && (
                    <MenuItem onClick={handleOpenModal} disableRipple>
                        <EditIcon />{t('chatbot.nodeMenu.edit')}
                    </MenuItem>
                )}
                <MenuItem
                    onClick={deleteNode}
                    disableRipple
                    sx={{
                        '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.08) !important',
                            color: '#dc2626 !important',
                            '& svg': { color: '#dc2626 !important' },
                        },
                    }}
                >
                    <DeleteIcon />{t('chatbot.nodeMenu.delete')}
                </MenuItem>
                <MenuItem onClick={duplicateNode} disableRipple>
                    <CopyIcon />{t('chatbot.nodeMenu.duplicate')}
                </MenuItem>
            </Menu>
            <Modal
                id={modalOpen ? 'simple-popover' : undefined}
                open={modalOpen}
                onClose={handleCloseModal}
                sx={{
                    margin: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <BTChatbotModal type={modalType()} onClose={handleCloseModal} id={id} />
            </Modal>
        </>
    );
}
